import { GooglePhotoItem, PickedImage } from '@photosi/expo-photosi-sdk/build/ImageLibrary';
import { logWarning } from '../logger';
import { ImagePickerAsset } from '../image-picker/ImagePicker';
import { ImageFileTypes, ImageURITypes } from '../image-picker/ImagePicker.constants';
import { fileToBase64 } from './fileToBase64';

const getTypeFromAsset = (asset: ImagePickerAsset | File): string | undefined => {
  let type = 'mimeType' in asset ? asset.mimeType : asset.type;

  if (!type || type.trim() === '') {
    const name =
      'fileName' in asset ? asset.fileName?.toLowerCase() : 'name' in asset ? asset.name?.toLowerCase() : undefined;

    if (!!name && (name.endsWith('.heic') || name.endsWith('.heif'))) {
      type = 'image/heic';
    } else {
      type = undefined;
    }
  }
  return type;
};

const getFileFromAsset = (asset: ImagePickerAsset | File): File | undefined => {
  return 'file' in asset ? (asset as ImagePickerAsset).file : (asset as File);
};

const getFilenameFromAsset = (asset: ImagePickerAsset | File) => {
  return 'fileName' in asset ? (asset as ImagePickerAsset).fileName : (asset as File).name;
};

const getUriFromAsset = async (asset: ImagePickerAsset | File): Promise<string | undefined> => {
  let uri: string | undefined;
  try {
    const fileURI = 'uri' in asset ? asset.uri : await fileToBase64(asset);
    if (typeof fileURI === 'string') {
      uri = fileURI;
    }
  } catch (error) {}

  return uri;
};

const isAValidImageFileType = (asset: ImagePickerAsset | File, newRoute?: boolean): boolean => {
  let isValid = false;

  const file = getFileFromAsset(asset);
  if (file) {
    const assetType = getTypeFromAsset(file);
    isValid = !!assetType && ImageFileTypes.some(type => assetType === type);
  }

  return isValid;
};

const isAValidImageURIType = (asset: ImagePickerAsset | File, uri?: string): boolean => {
  const assetType = getTypeFromAsset(asset);
  return typeof uri === 'string' && ImageURITypes.some(type => assetType === type || !!uri.match(`data:${type}`));
};

type AssetType = ImagePickerAsset | File | GooglePhotoItem;

const getPickedImageFrom = async (asset: AssetType, newRoute?: boolean): Promise<PickedImage | undefined> => {
  let image: PickedImage | undefined;

  if (asset && 'mediaFile' in asset && typeof asset['mediaFile'] === 'object') {
    // Google Photo Item
    image = {
      googlePhotoItem: asset as GooglePhotoItem,
    };
  } else {
    const assetFile = asset as ImagePickerAsset | File;
    if (isAValidImageFileType(assetFile, newRoute)) {
      image = { file: getFileFromAsset(assetFile) };
    } else {
      const uri = await getUriFromAsset(assetFile);
      if (isAValidImageURIType(assetFile, uri)) {
        image = { uri };
      }
    }

    if (typeof image !== 'undefined') {
      const fileName = getFilenameFromAsset(assetFile);
      if (fileName) {
        image = { ...image, fileName };
      }
    }
  }

  return image;
};

export type TFilesToPickedImagesOptions = {
  i18n: { t: (key: string) => string };
  errorCallback: (data: { errorImagesCount: number; errorMessage: string; areAllPhotosInvalid: boolean }) => void;
};

export const filesToPickedImages = async (
  assets: Array<AssetType>,
  { i18n, errorCallback }: TFilesToPickedImagesOptions,
  newRoute = false,
): Promise<PickedImage[]> => {
  const files: PickedImage[] = [];
  for (const asset of assets) {
    try {
      const image = await getPickedImageFrom(asset, newRoute);

      if (image) {
        files.push(image);
      } else {
        throw new Error(i18n.t('toast:invalid-photo-format'));
      }
    } catch (error) {
      logWarning(error, JSON.stringify(asset));
    }
  }

  const errorImagesCount = assets.length - files.length;

  if (errorImagesCount > 0) {
    const areAllPhotosInvalid = files.length === 0;
    const initialMessage = areAllPhotosInvalid
      ? i18n.t('toast:invalid-photo-format')
      : i18n.t('toast:invalid-of-some-photos-format');
    errorCallback({
      errorImagesCount,
      areAllPhotosInvalid,
      errorMessage: `${initialMessage}\n${i18n.t('toast:valid-photo-format-reminder')}`,
    });
  }
  return files;
};
